// ServiciosLanding.tsx — Estilo Services/AboutusProjects — Co Marca
import './PublicCatalogTheme.css';
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchPublicServices, PublicService as Servicio, PublicPrice as PrecioPlanServicio } from '../../../services/publicCatalogService';
import { fetchActiveCategories } from '../../../services/landing/categoryService';
import { Pagination } from '../../../components/paginationlanding/Pagination';
import { usePagination } from '../../../hooks/usePagination';
import { ChevronRight, Home, Zap, ArrowRight, MessageCircle, Info, PackageX } from 'lucide-react';
import BrandStripes from '../../../components/common/BrandStripes';

const colors = {
    primary: '#c1348b',
    primaryDark: '#8f1f64',
    secondary: '#61257b',
    secondaryLight: '#8b3fa8',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
* { box-sizing: border-box; }
@keyframes blobFloat {
    0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; }
    50%      { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; }
}
@keyframes slFadeUp {
    from { opacity:0; transform:translateY(24px); }
    to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
    0%,100% { box-shadow:0 0 0 0 rgba(193, 52, 139,.35); }
    50%     { box-shadow:0 0 0 6px rgba(193, 52, 139,0); }
}
@keyframes spin { to { transform:rotate(360deg); } }
.blob-sl  { animation: blobFloat 9s ease-in-out infinite; }
.sl-cta:hover { transform:translateY(-2px) !important; box-shadow:0 10px 28px rgba(193, 52, 139,.28) !important; }
@media (max-width:720px) {
    .sl-card-inner { flex-direction:column !important; }
    .sl-card-img   { flex:none !important; min-height:220px !important; width:100% !important; }
}
`;


function useInView(): [React.RefObject<HTMLDivElement>, boolean] {
    const ref = useRef<HTMLDivElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const r = ref.current.getBoundingClientRect();
            if (r.top < window.innerHeight) { setInView(true); return; }
        }
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);
    return [ref, inView];
}

// ══ SERVICIO CARD — estilo Services/AboutusProjects ══
function ServicioCard({ servicio, planes, index, categoryId, navigate }: {
    servicio: Servicio;
    planes: PrecioPlanServicio[];
    index: number;
    categoryId: string;
    navigate: (to: string) => void;
}) {
    const [ref, inView] = useInView();
    const [hov, setHov] = useState(false);
    const accentList = [colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight, colors.primaryDark];
    const accent = accentList[index % accentList.length];
    const precioDesde = planes.length > 0 ? Math.min(...planes.map(p => Number(p.precio_inicial))) : null;

    return (
        <div
            ref={ref}
            className="sl-card-inner"
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            onClick={() => navigate(`/servicio/${servicio.slug}`)}
            style={{
                display: 'flex', flexDirection: 'row', flexWrap: 'wrap',
                background: 'var(--catalog-surface)', borderRadius: '24px',
                border: hov ? `1.5px solid ${accent}35` : '1.5px solid var(--catalog-border)',
                boxShadow: hov ? `0 20px 56px ${accent}14, 0 6px 20px rgba(0,0,0,.06)` : '0 2px 16px rgba(0,0,0,.05)',
                overflow: 'hidden', cursor: 'pointer',
                transition: 'all .35s cubic-bezier(.16,1,.3,1)',
                transform: inView ? (hov ? 'translateY(-4px)' : 'translateY(0)') : 'translateY(32px)',
                opacity: inView ? 1 : 0,
                transitionDelay: `${index * .07}s`,
            }}
        >
            {/* ── Imagen ── */}
            <div
                className="sl-card-img"
                style={{
                    flex: '0 0 380px', minHeight: '280px', position: 'relative', overflow: 'hidden',
                    background: `linear-gradient(145deg,${accent}dd,${accent}88)`,
                }}
            >
                <img
                    src={servicio.imagen || '/placeholder-service.jpg'}
                    alt={servicio.nombre}
                    loading="lazy"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                        transform: hov ? 'scale(1.07)' : 'scale(1)',
                        transition: 'transform .5s cubic-bezier(.16,1,.3,1)',
                    }}
                />
                {/* Overlay lateral */}
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, transparent 60%, var(--catalog-surface) 100%)', pointerEvents: 'none' }} />

                {/* Badges */}
                <div style={{ position: 'absolute', top: '16px', left: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {planes.some(p => p.plan_nombre.toLowerCase() === 'premium') && (
                        <span style={{ background: `linear-gradient(135deg,${colors.accentDark},${colors.accent})`, color: '#fff', borderRadius: '100px', padding: '4px 14px', fontSize: '.7rem', fontWeight: '700', letterSpacing: '.06em', textTransform: 'uppercase', boxShadow: `0 4px 12px ${colors.accentDark}40`, fontFamily: "'DM Sans',sans-serif" }}>
                            Popular
                        </span>
                    )}
                </div>

                {/* Precio desde */}
                {precioDesde !== null && (
                    <div style={{ position: 'absolute', bottom: '16px', left: '16px', background: 'rgba(15,23,42,.72)', backdropFilter: 'blur(8px)', borderRadius: '12px', padding: '8px 14px', fontFamily: "'DM Sans',sans-serif" }}>
                        <p style={{ fontSize: '.6rem', fontWeight: '700', color: 'rgba(255,255,255,.6)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '2px' }}>Desde</p>
                        <p style={{ fontSize: '1.1rem', fontWeight: '900', color: '#fff', letterSpacing: '-.02em' }}>
                            {precioDesde} <span style={{ fontSize: '.65rem', fontWeight: '600', opacity: .7 }}>Bs.</span>
                        </p>
                    </div>
                )}

                {/* Barra acento bottom al hover */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '4px', background: `linear-gradient(90deg,${accent},${accent}88)`, opacity: hov ? 1 : 0, transition: 'opacity .3s' }} />
            </div>

            {/* ── Contenido ── */}
            <div style={{ flex: 1, minWidth: '280px', padding: 'clamp(28px,4vw,40px) clamp(24px,3vw,40px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {/* Tag */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                    <span style={{ fontSize: '.7rem', fontWeight: '700', letterSpacing: '.14em', textTransform: 'uppercase', color: `var(--catalog-accent, ${accent})` }}>Servicio</span>
                    <div style={{ height: '2px', width: '28px', borderRadius: '2px', background: accent }} />
                    {planes.length > 0 && (
                        <span style={{ fontSize: '.65rem', fontWeight: '700', color: 'var(--catalog-subtle, #94a3b8)', background: 'var(--catalog-page)', border: '1px solid var(--catalog-border)', borderRadius: '6px', padding: '2px 8px' }}>
                            {planes.length} plan{planes.length !== 1 ? 'es' : ''}
                        </span>
                    )}
                </div>

                {/* Título */}
                <h3 style={{ fontSize: 'clamp(1.3rem,2.2vw,1.75rem)', fontWeight: '800', color: 'var(--catalog-text, #0f172a)', lineHeight: 1.2, letterSpacing: '-.02em', marginBottom: '14px', fontFamily: "'DM Sans',sans-serif" }}>
                    <Link to={`/servicio/${servicio.slug}`} onClick={event => event.stopPropagation()} style={{color: 'inherit', textDecoration: 'none'}}>{servicio.nombre}</Link>
                </h3>

                {/* Descripción recortada */}
                <p style={{ fontSize: '.97rem', color: 'var(--catalog-muted, #64748b)', lineHeight: 1.8, marginBottom: '28px', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }}>
                    {servicio.descripcion}
                </p>

                {/* Plan pills preview */}
                {planes.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', marginBottom: '24px' }}>
                        {planes.slice(0, 3).map((p, i) => {
                            const pc = [colors.secondary, colors.accentDark, colors.secondaryLight][i % 3];
                            return (
                                <span key={`${p.plan_nombre}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: `${pc}09`, border: `1px solid ${pc}22`, borderRadius: '8px', padding: '5px 10px', fontSize: '.72rem', fontWeight: '700', color: `var(--catalog-accent, ${pc})`, fontFamily: "'DM Sans',sans-serif" }}>
                                    {p.plan_nombre} · {p.precio_inicial} Bs.
                                </span>
                            );
                        })}
                        {planes.length > 3 && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--catalog-page)', border: '1px solid var(--catalog-border)', borderRadius: '8px', padding: '5px 10px', fontSize: '.72rem', fontWeight: '700', color: 'var(--catalog-subtle, #94a3b8)' }}>
                                +{planes.length - 3} más
                            </span>
                        )}
                    </div>
                )}

                {/* CTA */}
                <div>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        background: `linear-gradient(135deg,${accent},${accent}cc)`,
                        color: '#fff', borderRadius: '12px',
                        padding: '11px 24px', fontSize: '.88rem', fontWeight: '700',
                        letterSpacing: '.01em', fontFamily: "'DM Sans',sans-serif",
                        boxShadow: `0 4px 14px ${accent}35`,
                        transition: 'all .2s',
                        transform: hov ? 'translateX(4px)' : 'translateX(0)',
                    }}>
                        Ver detalles y precios <ArrowRight size={14} strokeWidth={2.5} />
                    </span>
                </div>
            </div>
        </div>
    );
}

// ══ HELPERS ══
const LoadingSpinner = () => (
    <div className="public-catalog" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px', fontFamily: "'DM Sans',sans-serif", background: 'var(--catalog-page)' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '50%', border: `3px solid ${colors.primary}18`, borderTop: `3px solid ${colors.primary}`, animation: 'spin .7s linear infinite' }} />
        <p style={{ fontSize: '.85rem', fontWeight: '600', color: 'var(--catalog-subtle, #94a3b8)' }}>Cargando servicios...</p>
        <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
);
const ErrorDisplay = ({ message }: { message: string }) => (
    <div className="public-catalog" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans',sans-serif" }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: `${colors.primary}08`, border: `1.5px solid ${colors.primary}22`, borderRadius: '14px', padding: '18px 24px' }}>
            <Info size={18} color={colors.primary} />
            <span style={{ fontSize: '.88rem', fontWeight: '700', color: `var(--catalog-accent, ${colors.primaryDark})` }}>{message}</span>
        </div>
    </div>
);
const EmptyState = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '64px 20px', background: 'var(--catalog-surface)', borderRadius: '20px', border: '1.5px dashed var(--catalog-border)', gap: '12px' }}>
        <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: `${colors.accentDark}08`, border: `1.5px solid ${colors.accentDark}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PackageX size={26} color={colors.accentDark} strokeWidth={1.5} />
        </div>
        <p style={{ fontSize: '1rem', fontWeight: '800', color: 'var(--catalog-text, #374151)' }}>No hay servicios disponibles</p>
        <p style={{ fontSize: '.82rem', color: 'var(--catalog-subtle, #94a3b8)', fontWeight: '500', textAlign: 'center', maxWidth: '300px', lineHeight: 1.6 }}>Consulta con nosotros sobre opciones personalizadas.</p>
    </div>
);

// ══ MAIN ══
const ServiciosLanding = () => {
    const { categoryId, categorySlug } = useParams<{ categoryId: string; categorySlug: string }>();
    const navigate = useNavigate();
    const [servicios, setServicios] = useState<Servicio[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [heroVisible, setHeroVisible] = useState(false);
    const heroRef = useRef<HTMLDivElement>(null);
    const itemsPerPage = 4;

    useEffect(() => { setTimeout(() => setHeroVisible(true), 80); }, []);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        Promise.all([fetchPublicServices(categorySlug || categoryId || '', !categorySlug), fetchActiveCategories()])
            .then(([data, categories]) => {
                if (!active) return;
                const category = categories.find(item => categorySlug ? item.slug === categorySlug : String(item.id) === categoryId);
                if (!category) {setError('Categoría no encontrada'); return;}
                setServicios(data);
                setCategoryName(category.nombre);
                if (!categorySlug) navigate(`/servicios/categoria/${category.slug}`, {replace: true});
            })
            .catch(() => {if (active) setError('No se pudieron cargar los servicios. Intenta nuevamente.');})
            .finally(() => {if (active) setLoading(false);});
        return () => {active = false;};
    }, [categoryId, categorySlug, navigate]);

    const { currentItems, currentPage, totalPages, handlePageChange } = usePagination<Servicio>(servicios, itemsPerPage);

    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorDisplay message={error} />;

    return (
        <div className="public-catalog" style={{ fontFamily: "'DM Sans',sans-serif", background: 'var(--catalog-page)', minHeight: '100vh', overflowX: 'clip' }}>
            <style>{css}</style>

            {/* ══ HERO ══ */}
            <section style={{ background: 'var(--catalog-hero)', padding: 'clamp(100px,14vw,140px) 32px clamp(60px,8vw,80px)', position: 'relative', overflow: 'hidden' }}>
                <div className="blob-sl" style={{ position: 'absolute', top: '-80px', right: '-80px', width: '420px', height: '420px', background: `linear-gradient(135deg,${colors.primary}14,${colors.secondary}0e)`, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: '20%', left: '4%', opacity: .1, display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '14px', pointerEvents: 'none' }}>
                    {[...Array(25)].map((_, i) => <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: colors.primaryDark }} />)}
                </div>

                <div ref={heroRef} style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                    {/* Breadcrumb */}
                    <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '.75rem', fontWeight: '600', color: 'var(--catalog-subtle, #94a3b8)', marginBottom: '28px', flexWrap: 'wrap', opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(16px)', transition: 'all .6s cubic-bezier(.16,1,.3,1)' }}>
                        {[
                            { label: 'Inicio', href: '/', Icon: Home },
                            { label: 'Servicios', href: '/servicios/cowork', Icon: null },
                            { label: categoryName || '...', href: null, Icon: null },
                        ].map((item, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <ChevronRight size={11} color="#e2e8f0" />}
                                {item.href ? (
                                    <a href={item.href} style={{ display: 'flex', alignItems: 'center', gap: '3px', color: 'var(--catalog-subtle, #94a3b8)', textDecoration: 'none', transition: 'color .2s' }}
                                        onMouseEnter={e => { e.currentTarget.style.color = `var(--catalog-accent, ${colors.primary})`; }}
                                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--catalog-subtle, #94a3b8)'; }}>
                                        {item.Icon && <item.Icon size={11} />} {item.label}
                                    </a>
                                ) : (
                                    <span style={{ color: `var(--catalog-accent, ${colors.primary})`, fontWeight: '700' }}>{item.label}</span>
                                )}
                            </React.Fragment>
                        ))}
                    </nav>

                    <div style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'all .65s cubic-bezier(.16,1,.3,1) .05s', marginBottom: '16px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: `${colors.primary}14`, border: `1.5px solid ${colors.primary}28`, borderRadius: '100px', padding: '6px 18px', fontSize: '.78rem', fontWeight: '700', color: `var(--catalog-accent, ${colors.primaryDark})`, letterSpacing: '.1em', textTransform: 'uppercase' }}>
                            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: colors.primary, animation: 'pulseD 2s infinite', display: 'inline-block' }} />
                            Co Marca Group
                        </span>
                    </div>

                    <h1 style={{ fontSize: 'clamp(2.4rem,5vw,3.8rem)', fontWeight: '900', color: 'var(--catalog-text, #0f172a)', lineHeight: 1.08, letterSpacing: '-.025em', marginBottom: '20px', opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(22px)', transition: 'all .7s cubic-bezier(.16,1,.3,1) .1s' }}>
                        Servicios de <span style={{ background: 'var(--catalog-title-gradient)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{categoryName || '...'}</span>
                    </h1>

                    <p style={{ fontSize: '1.05rem', color: 'var(--catalog-muted, #64748b)', maxWidth: '500px', margin: '0 auto 20px', lineHeight: 1.7, fontWeight: '400', opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(20px)', transition: 'all .7s cubic-bezier(.16,1,.3,1) .18s' }}>
                        Explora cada servicio en detalle, conoce sus planes y encuentra el espacio perfecto para ti.
                    </p>

                    {/* Stats row */}
                    <div style={{ display: 'inline-flex', background: 'var(--catalog-surface)', borderRadius: '18px', border: '1.5px solid var(--catalog-border)', boxShadow: '0 2px 16px rgba(0,0,0,.05)', overflow: 'hidden', marginBottom: '28px', opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(18px)', transition: 'all .7s cubic-bezier(.16,1,.3,1) .22s' }}>
                        {[
                            { value: servicios.length.toString(), label: 'Servicios', color: colors.primary },
                            { value: servicios.reduce((total, service) => total + service.planes.length, 0).toString(), label: 'Planes', color: colors.secondary },
                            { value: '300+', label: 'Miembros', color: colors.secondaryLight },
                        ].map((s, i) => (
                            <div key={s.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 24px', borderRight: i < 2 ? '1.5px solid var(--catalog-border)' : 'none' }}>
                                <div style={{ fontSize: '1.6rem', fontWeight: '900', color: `var(--catalog-accent, ${s.color})`, lineHeight: 1, letterSpacing: '-.03em' }}>{s.value}</div>
                                <div style={{ fontSize: '.62rem', color: 'var(--catalog-subtle, #94a3b8)', fontWeight: '600', marginTop: '4px' }}>{s.label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Barra colores */}
                    <div style={{ display: 'flex', justifyContent: 'center', opacity: heroVisible ? 1 : 0, transition: 'opacity .7s .3s' }}>
                        <BrandStripes variant="scroll-prompt" size="md" promptText="Descubre los espacios" />
                    </div>
                </div>
            </section>

            {/* ══ CARDS ══ */}
            <section style={{ padding: 'clamp(48px,7vw,72px) clamp(20px,4vw,40px) clamp(60px,9vw,100px)', maxWidth: '1200px', margin: '0 auto' }}>
                {servicios.length === 0 ? (
                    <EmptyState />
                ) : (
                    <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            {currentItems.map((servicio, idx) => (
                                <ServicioCard
                                    key={servicio.slug}
                                    servicio={servicio}
                                    planes={servicio.planes}
                                    index={idx}
                                    categoryId={categoryId || ''}
                                    navigate={navigate}
                                />
                            ))}
                        </div>
                        {totalPages > 1 && (
                            <div style={{ marginTop: '48px', display: 'flex', justifyContent: 'center' }}>
                                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                            </div>
                        )}
                    </>
                )}

                {/* CTA Banner */}
                <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '24px', marginTop: '64px', background: `linear-gradient(135deg,${colors.primaryDark},${colors.primary} 60%,#ff6b8a)`, padding: 'clamp(36px,5vw,56px) clamp(28px,4vw,48px)', boxShadow: `0 16px 52px ${colors.primary}22` }}>
                    <div className="blob-sl" style={{ position: 'absolute', top: '-50px', right: '-50px', width: '280px', height: '280px', pointerEvents: 'none', background: 'rgba(255,255,255,.06)' }} />
                    <div style={{ position: 'relative', maxWidth: '580px', margin: '0 auto', textAlign: 'center' }}>
                        <div style={{ width: '52px', height: '52px', borderRadius: '16px', background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <Zap size={24} color="#fff" strokeWidth={1.8} />
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.5rem,3vw,2.2rem)', fontWeight: '900', color: '#fff', letterSpacing: '-.03em', lineHeight: 1.1, marginBottom: '10px' }}>¿Te interesa algún servicio?</h2>
                        <p style={{ fontSize: '.95rem', color: 'rgba(255,255,255,.8)', fontWeight: '500', lineHeight: 1.65, marginBottom: '24px' }}>Agenda una visita y conoce nuestros espacios de primera mano. Un asesor te guiará en todo momento.</p>
                        <button className="sl-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'var(--catalog-surface)', color: `var(--catalog-accent, ${colors.primary})`, padding: '13px 28px', borderRadius: '12px', border: 'none', fontSize: '.88rem', fontWeight: '800', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", boxShadow: '0 4px 16px rgba(0,0,0,.10)', transition: 'all .25s cubic-bezier(.16,1,.3,1)' }}>
                            <MessageCircle size={16} strokeWidth={2} /> Contactar un asesor <ArrowRight size={15} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>

                {/* Barra final */}
                <div style={{ marginTop: '64px', display: 'flex', justifyContent: 'center' }}>
                    <BrandStripes size="md" />
                </div>
            </section>
        </div>
    );
};

export default ServiciosLanding;